import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import toast from "react-hot-toast";
import {
    getPropertyOffers,
    getPropertyVendorStatus,
    confirmMutualAcceptance,
    updateOffer,
} from "../../../api/propertyManagement";
import SendToVendorModal from "./SendToVendorModal";
import SendToBuyerModal from "./SendToBuyerModal";
import CounterToBuyerModal from "./CounterToBuyerModal";
import OfferTimelineModal from "./OfferTimelineModal";

/**
 * The offer negotiation workflow — status-gated action buttons plus the modals
 * they open — as one reusable block.
 *
 * It is rendered both in the View Buyer Offer pop-up footer and directly on the
 * Offers-tab rows of the Buyers Enquiries & Offers page (Christian Gravias,
 * Monday 13099979503: the offer row buttons should be the negotiation actions,
 * not the generic Add to Property / Database / Ignore set). Keeping it in one
 * place means both surfaces gate and behave identically.
 *
 * Which buttons show is driven entirely by the offer's status, so a brand-new
 * (SUBMITTED) offer shows Send to Vendor / Send Counter Offer / Decline / Fill
 * Out Contract, and later stages surface Send to Buyer and Confirm Acceptance.
 *
 * Props:
 *   offer            the CRM Offer (needs at least id + status; amounts feed the
 *                    counter prefill and the contract).
 *   propertyId       the listing the offer sits on (for vendor lookup + contract).
 *   buyerName/buyerEmail  used to prefill the Fill Out Contract flow.
 *   showHistory      render the "View History" button (default false — it is left
 *                    off the rows per CG, kept in the modal footer).
 *   size             'sm' for the compact row buttons, 'md' for the modal footer.
 *   counterPrefill   extra fields merged into the counter modal (the modal passes
 *                    the agent's on-screen edits; rows pass nothing).
 *   onOfferUpdated(offer)  fired whenever an action changes the offer, so the
 *                    parent can refresh its own copy.
 *   onDecline()      fired after a successful decline (parent removes the row /
 *                    closes the modal).
 *   onBeforeContractNav()  fired right before navigating to the eSign contract
 *                    flow (the modal passes onClose so it shuts first).
 */
const STATES_TERMINAL = ["ACCEPTED", "REJECTED", "EXPIRED", "WITHDRAWN", "BUYER_WITHDRAWN"];
const STATES_SEND_TO_VENDOR = ["SUBMITTED", "UNDER_REVIEW", "BUYER_COUNTERED"];
const STATES_SEND_TO_BUYER = ["VENDOR_COUNTERED", "VENDOR_ACCEPTED", "VENDOR_DECLINED", "COUNTER_OFFER"];
const STATES_CONFIRM = ["VENDOR_ACCEPTED", "BUYER_ACCEPTED_COUNTER"];

const OfferWorkflowActions = ({
    offer,
    propertyId,
    buyerName = "",
    buyerEmail = "",
    showHistory = false,
    size = "md",
    counterPrefill = null,
    onOfferUpdated,
    onDecline,
    onBeforeContractNav,
}) => {
    const navigate = useNavigate();

    // A local copy so the buttons re-gate the instant an action lands, without
    // waiting on a parent round-trip.
    const [live, setLive] = useState(offer);
    useEffect(() => {
        setLive(offer);
    }, [offer]);

    const [vendorStatus, setVendorStatus] = useState(null);
    const [sendingToVendor, setSendingToVendor] = useState(null);
    const [sendingToBuyer, setSendingToBuyer] = useState(null);
    const [counteringToBuyer, setCounteringToBuyer] = useState(null);
    const [timelineOffer, setTimelineOffer] = useState(null);
    const [finalizingOffer, setFinalizingOffer] = useState(null);
    const [finalizing, setFinalizing] = useState(false);
    const [declining, setDeclining] = useState(false);
    const [finalizeNote, setFinalizeNote] = useState("");

    useEffect(() => {
        if (!propertyId) return undefined;
        let cancelled = false;
        getPropertyVendorStatus(propertyId)
            .then((r) => {
                if (!cancelled) setVendorStatus(r?.data || null);
            })
            .catch(() => {
                if (!cancelled) setVendorStatus(null);
            });
        return () => {
            cancelled = true;
        };
    }, [propertyId]);

    // Pull the canonical offer back after an action so the status (and figures)
    // reflect what the server actually recorded, then bubble it up.
    const refresh = useCallback(
        async (immediate) => {
            if (immediate) {
                setLive((prev) => ({ ...(prev || {}), ...immediate }));
                onOfferUpdated?.(immediate);
            }
            if (!propertyId || !live?.id) return;
            try {
                const res = await getPropertyOffers(propertyId);
                const list = res?.data?.results || res?.data || [];
                const found = list.find((o) => o.id === live.id);
                if (found) {
                    setLive(found);
                    onOfferUpdated?.(found);
                }
            } catch (_) {
                /* the immediate patch already applied; a failed refetch is not fatal */
            }
        },
        [propertyId, live?.id, onOfferUpdated],
    );

    const handleDecline = async () => {
        if (!live?.id) return;
        setDeclining(true);
        try {
            const res = await updateOffer(live.id, { status: "REJECTED" });
            const data = res?.data || {};
            setLive((prev) => ({ ...(prev || {}), ...data }));
            onOfferUpdated?.(data);
            toast.success("Offer declined. It will show as Declined on the property.");
            onDecline?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to decline offer.");
        } finally {
            setDeclining(false);
        }
    };

    const handleFillOutContract = () => {
        const params = new URLSearchParams({
            newFlow: "contract",
            propertyId: String(propertyId),
            buyerName: buyerName || "",
            buyerEmail: buyerEmail || "",
        });
        onBeforeContractNav?.();
        // The offer travels in navigation state so its figures prefill the
        // contract (CG, 16 Aug 2026).
        navigate(`/esign/agreements?${params.toString()}`, { state: { offer: live || null } });
    };

    if (!live) return null;

    const status = live.status;
    const btn = size === "sm"
        ? "px-3 py-1.5 text-xs font-bold rounded-lg transition-all inline-flex items-center justify-center gap-1 disabled:opacity-50"
        : "px-8 py-3 text-sm font-bold rounded-xl shadow-md transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50 font-urbanist";

    return (
        <>
            {STATES_SEND_TO_VENDOR.includes(status) && (
                <button
                    type="button"
                    onClick={() => setSendingToVendor(live)}
                    className={`${btn} bg-[#55C5D0] text-white hover:bg-[#46acb6]`}
                >
                    Send to Vendor
                </button>
            )}
            {STATES_SEND_TO_BUYER.includes(status) && (
                <button
                    type="button"
                    onClick={() => setSendingToBuyer(live)}
                    className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}
                >
                    Send to Buyer
                </button>
            )}
            {!STATES_TERMINAL.includes(status) && (
                <button
                    type="button"
                    onClick={() => setCounteringToBuyer({ ...live, ...(counterPrefill || {}) })}
                    className={`${btn} bg-orange-600 text-white hover:bg-orange-700`}
                >
                    Send Counter Offer
                </button>
            )}
            {STATES_CONFIRM.includes(status) && (
                <button
                    type="button"
                    onClick={() => setFinalizingOffer(live)}
                    className={`${btn} bg-emerald-800 text-white hover:bg-emerald-900`}
                >
                    <CheckCircle2 size={size === "sm" ? 14 : 16} />
                    Confirm Acceptance
                </button>
            )}
            {!STATES_TERMINAL.includes(status) && (
                <button
                    type="button"
                    onClick={handleDecline}
                    disabled={declining}
                    className={`${btn} bg-red-600 text-white hover:bg-red-700`}
                >
                    {declining ? <Loader2 size={size === "sm" ? 14 : 16} className="animate-spin" /> : <XCircle size={size === "sm" ? 14 : 16} />}
                    Decline Offer
                </button>
            )}
            {showHistory && (
                <button
                    type="button"
                    onClick={() => setTimelineOffer(live)}
                    className={
                        size === "sm"
                            ? "px-3 py-1.5 text-xs font-bold text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
                            : "px-8 py-3 text-sm font-bold text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all font-urbanist"
                    }
                >
                    View History
                </button>
            )}
            <button
                type="button"
                onClick={handleFillOutContract}
                className={`${btn} bg-green-600 text-white hover:bg-green-700`}
            >
                Fill Out Contract
            </button>

            <SendToVendorModal
                isOpen={!!sendingToVendor}
                offer={sendingToVendor}
                vendorEmail={vendorStatus?.vendor?.email || vendorStatus?.owner_email}
                vendorName={vendorStatus?.vendor?.name}
                vendorPendingActivation={vendorStatus?.state === "pending"}
                onClose={() => setSendingToVendor(null)}
                onSent={(data) => {
                    void refresh(data);
                    if (vendorStatus?.state === "not_invited" && propertyId) {
                        getPropertyVendorStatus(propertyId)
                            .then((r) => setVendorStatus(r?.data || null))
                            .catch(() => {});
                    }
                }}
            />

            <SendToBuyerModal
                isOpen={!!sendingToBuyer}
                offer={sendingToBuyer}
                onClose={() => setSendingToBuyer(null)}
                onSent={(data) => void refresh(data)}
            />

            <CounterToBuyerModal
                isOpen={!!counteringToBuyer}
                offer={counteringToBuyer}
                onClose={() => setCounteringToBuyer(null)}
                onSent={(data) => void refresh(data)}
            />

            <OfferTimelineModal
                isOpen={!!timelineOffer}
                offer={timelineOffer}
                onClose={() => setTimelineOffer(null)}
            />

            {finalizingOffer && (
                <div
                    className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                    onClick={(e) => {
                        if (!finalizing && e.target === e.currentTarget) setFinalizingOffer(null);
                    }}
                    role="presentation"
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden p-6 border border-gray-100"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm Mutual Acceptance</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Move offer <strong>{finalizingOffer.reference_number}</strong> to <strong>Accepted</strong> and mark the property as{" "}
                            <strong>Under Contract</strong>. Other open offers on this listing will be expired and parties notified by email.
                        </p>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-1 block">
                            Optional note (visible to buyer + vendor)
                        </label>
                        <textarea
                            value={finalizeNote}
                            onChange={(e) => setFinalizeNote(e.target.value)}
                            className="w-full border-2 border-gray-100 rounded-xl p-3 bg-gray-50 min-h-[80px] text-sm focus:bg-white focus:border-emerald-500 transition-all outline-none resize-none mb-4"
                            placeholder="e.g. Contracts will be issued today."
                        />
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                disabled={finalizing}
                                onClick={() => setFinalizingOffer(null)}
                                className="px-4 py-2 rounded-lg border-2 border-gray-200 text-gray-700 text-xs font-bold uppercase tracking-wider hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={finalizing}
                                onClick={async () => {
                                    if (!finalizingOffer) return;
                                    setFinalizing(true);
                                    try {
                                        const res = await confirmMutualAcceptance(finalizingOffer.id, { note: finalizeNote });
                                        const data = res?.data || {};
                                        await refresh(data);
                                        toast.success(
                                            `Offer accepted. Property is now Under Contract${
                                                data.expired_other_offers ? `. ${data.expired_other_offers} other offer(s) expired.` : "."
                                            }`,
                                        );
                                        setFinalizingOffer(null);
                                        setFinalizeNote("");
                                    } catch (err) {
                                        toast.error(err?.response?.data?.detail || "Failed to confirm mutual acceptance.");
                                    } finally {
                                        setFinalizing(false);
                                    }
                                }}
                                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50"
                            >
                                {finalizing ? "Confirming…" : "Confirm & Move to Under Contract"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default OfferWorkflowActions;
